import { useParams } from 'react-router-dom';
import { Container } from '@vnetwork/ui/layout';
import { Avatar, Badge, Button, Card, CardBody, Skeleton, Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { DetailHeader } from '@/components/detail-header';
import { useEmployeeDetail } from '@/api/employees';
import { env } from '@/config/env';
import { EmployeeTabPersonal } from './employee-tab-personal';
import { EmployeeTabIdentification } from './employee-tab-identification';
import { EmployeeTabAddress } from './employee-tab-address';
import { EmployeeTabFinance } from './employee-tab-finance';
import { EmployeeTabExperience } from './employee-tab-experience';
import { EmployeeTabProfile } from './employee-tab-profile';
import { EmployeeTabWorkInfo } from './employee-tab-work-info';
import { EmployeeTabCareerPath } from './employee-tab-career-path';

const CONTRACT_LABEL: Record<string, string> = {
  intern: 'Intern',
  probation: 'Probation',
  official: 'Official',
  collaborator: 'Collaborator',
  advisor: 'Advisor',
};

/**
 * Employee detail page — profile header (DetailHeader + Avatar + badges) over
 * an 8-tab InfoGrid: Personal / Identification / Address / Finance / Experience
 * / Profile / Work Info / Career Path. Read-only; mutations are deferred.
 * Route: /employees/:id
 */
export default function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const employeeId = id ? Number(id) : undefined;

  const { data: emp, isLoading, isError, refetch } = useEmployeeDetail(employeeId);

  if (isError) {
    return (
      <Container size="full">
        <Alert
          tone="danger"
          title="Failed to load employee"
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      </Container>
    );
  }

  if (isLoading || !emp) {
    return (
      <Container size="full">
        <Card flat>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <Skeleton style={{ height: 32, maxWidth: 320 }} />
            <Skeleton style={{ height: 24, maxWidth: 240 }} />
          </CardBody>
        </Card>
      </Container>
    );
  }

  const avatarSrc = emp.employee_avatar
    ? `${env.storage.baseUrl}/${emp.employee_avatar}`
    : undefined;

  const statusTone = emp.employee_status === 1 ? 'success' : 'danger';
  const statusLabel = emp.employee_status === 1 ? 'Active' : 'Inactive';
  const contractLabel = CONTRACT_LABEL[emp.employee_contract_type] ?? emp.employee_contract_type;

  return (
    <Container size="full">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <DetailHeader
          parent={{ label: 'Employees', to: '/employees' }}
          current={emp.employee_name}
          idPrefix={emp.employee_code ? `#${emp.employee_code}` : undefined}
          title={emp.employee_name}
          meta={
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
              <StatusPill tone={statusTone}>{statusLabel}</StatusPill>
              {contractLabel ? <Badge tone="neutral">{contractLabel}</Badge> : null}
              {emp.employee_seniority_level ? (
                <Badge tone="info">{emp.employee_seniority_level}</Badge>
              ) : null}
            </div>
          }
        />

        {/* Identity card */}
        <Card flat>
          <CardBody>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-4)' }}>
              <Avatar
                name={emp.employee_name}
                src={avatarSrc}
                size={64}
                appearance="solid"
                tone="accent"
              />
              <div style={{ display: 'grid', gap: 'var(--vn-space-1)', minWidth: 0 }}>
                <span style={{ fontSize: 'var(--vn-font-size-lg)', fontWeight: 'var(--vn-font-weight-semibold)', color: 'var(--vn-fg-default)' }}>
                  {emp.employee_name}
                </span>
                <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                  {[emp.position_name, emp.department_name].filter(Boolean).join(' • ')}
                </span>
                <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-subtle)' }}>
                  {emp.employee_email_company}
                </span>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Tabbed detail */}
        <Card>
          <CardBody>
            <Tabs defaultValue="personal">
              <TabsList style={{ marginBottom: 'var(--vn-space-4)' }}>
                <TabsTrigger value="personal">Personal</TabsTrigger>
                <TabsTrigger value="identification">Identification</TabsTrigger>
                <TabsTrigger value="address">Address</TabsTrigger>
                <TabsTrigger value="finance">Finance</TabsTrigger>
                <TabsTrigger value="experience">Experience</TabsTrigger>
                <TabsTrigger value="profile">Profile</TabsTrigger>
                <TabsTrigger value="work-info">Work Info</TabsTrigger>
                <TabsTrigger value="career-path">Career Path</TabsTrigger>
              </TabsList>

              <TabsContent value="personal">
                <EmployeeTabPersonal emp={emp} />
              </TabsContent>
              <TabsContent value="identification">
                <EmployeeTabIdentification emp={emp} />
              </TabsContent>
              <TabsContent value="address">
                <EmployeeTabAddress emp={emp} />
              </TabsContent>
              <TabsContent value="finance">
                <EmployeeTabFinance emp={emp} />
              </TabsContent>
              <TabsContent value="experience">
                <EmployeeTabExperience emp={emp} />
              </TabsContent>
              <TabsContent value="profile">
                <EmployeeTabProfile emp={emp} />
              </TabsContent>
              <TabsContent value="work-info">
                <EmployeeTabWorkInfo emp={emp} />
              </TabsContent>
              <TabsContent value="career-path">
                {employeeId ? <EmployeeTabCareerPath employeeId={employeeId} /> : null}
              </TabsContent>
            </Tabs>
          </CardBody>
        </Card>
      </div>
    </Container>
  );
}
